import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactElement } from "react";
import type {
  AlwaysAiUser,
  MyAlertsResponse,
  NotificationDefaultsResponse,
} from "../../../shared/api-types";
import { NotFound } from "@/components/not-found";
import { BreadcrumbSetter } from "@/components/v2/breadcrumb";
import {
  DefaultsEditor,
  MyAlertsEditor,
  NotificationSettingsHeading,
  RdPicker,
  SettingsCard,
  type SaveStatus,
} from "@/components/v2/notification-settings-parts";
import { QueryErrorState } from "@/components/v2/query-error-state";
import { useShellFrame } from "@/components/v2/shell-layout";
import { authenticatedApi } from "@/lib/api";
import { deviceTimezone } from "@/lib/appointments";
import {
  canEditOwnNotificationSettings,
  canManagePracticeDefaults,
  changedByOthers,
  conflictCurrent,
  draftChanges,
  draftProblem,
  isMissingRdProfile,
  myAlertsConflictCurrent,
  myAlertsDraft,
  myAlertsProblem,
  myAlertsSave,
  notificationSettingsPageState,
  startingDraft,
  type DefaultsChange,
  type DefaultsDraft,
  type DefaultsTarget,
  type DraftRow,
  type EditorLayer,
  type MyAlertsDraft,
} from "@/lib/notification-settings-client";
import { NOTIFICATIONS_FLAG, useFeatures } from "@/lib/use-features";
import {
  useMyAlerts,
  useNotificationDefaults,
  useNotificationDefaultsRds,
  useSaveMyAlerts,
  useSaveNotificationDefaults,
} from "@/lib/use-notification-settings";

// /settings/notifications (NO-834): the viewer's notification settings, from
// the account menu, in the single-rail frame only. Blank while the flags and
// the viewer load; the router's 404 for anyone the menu would not offer it to.

const PAGE_FRAME = "mx-auto max-w-[880px] px-6 pb-12 pt-8";
const SAVE_FAILED = "We could not save these settings. Try again.";

function useCurrentUser() {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ["current-user"],
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
  });
}

export function NotificationSettingsPage(): ReactElement | null {
  const frame = useShellFrame();
  const features = useFeatures();
  const me = useCurrentUser();
  const user = me.data?.user;
  const state = notificationSettingsPageState({
    frame,
    featuresLoading: features.isPending || me.isPending,
    notificationsEnabled: features.data?.data[NOTIFICATIONS_FLAG] === true,
    user,
  });

  if (state === "blank") return null;

  if (state === "not_found" || !user) return <NotFound />;

  return <NotificationSettingsContent user={user} />;
}

function NotificationSettingsContent({ user }: { user: AlwaysAiUser }): ReactElement {
  return (
    <div className={PAGE_FRAME}>
      <BreadcrumbSetter segments={[{ label: "Notification settings" }]} />
      <NotificationSettingsHeading />
      {canEditOwnNotificationSettings(user) ? (
        <SettingsCard
          id="my-alerts"
          title="My alerts"
          description="Alerts about your own visits, by email and in your Notifications."
        >
          <MyAlerts />
        </SettingsCard>
      ) : null}
      {canEditOwnNotificationSettings(user) ? (
        <SettingsCard
          id="client-defaults"
          title="Defaults for my Clients"
          description="What your Clients get by email unless they chose otherwise. Anything you leave on the practice default follows it."
        >
          <OwnDefaults />
        </SettingsCard>
      ) : null}
      {canManagePracticeDefaults(user) ? <PracticeDefaultsCard /> : null}
    </div>
  );
}

function MissingRdProfile({ what }: { what: string }): ReactElement {
  return (
    <p className="text-body text-muted-foreground">
      Your account is not linked to an RD profile yet, so there are no {what} to set.
      Ask an admin to link it.
    </p>
  );
}

/** The editor state over the RD's own alerts: what it opened, its draft and its save. */
function useMyAlertsEditor() {
  const read = useMyAlerts(true);
  const { mutation, replace } = useSaveMyAlerts();
  const [opened, setOpened] = useState<MyAlertsResponse | null>(null);
  const [draft, setDraft] = useState<MyAlertsDraft | null>(null);
  const [status, setStatus] = useState<SaveStatus>({ kind: "idle" });

  useEffect(() => {
    if (!read.data) return;

    setOpened(read.data);
    setDraft(myAlertsDraft(read.data));
  }, [read.data]);

  const save = (from: MyAlertsResponse, next: MyAlertsDraft): void => {
    setStatus({ kind: "saving" });
    mutation.mutate(myAlertsSave(from, next), {
      onSuccess: (saved) => {
        setStatus({ kind: "saved", changedByOthers: [] });
        replace(saved);
      },
      onError: (error) => {
        const current = myAlertsConflictCurrent(error);
        setStatus(
          current === null
            ? { kind: "failed", message: saveFailure(error) }
            : { kind: "conflict" }
        );
        if (current !== null) replace(current);
      },
    });
  };

  const edit = (next: MyAlertsDraft): void => {
    setDraft(next);
    if (status.kind === "saved") setStatus({ kind: "idle" });
  };

  return { read, opened, draft, status, save, edit };
}

function MyAlerts(): ReactElement | null {
  const { read, opened, draft, status, save, edit } = useMyAlertsEditor();

  if (read.isError) {
    if (isMissingRdProfile(read.error)) return <MissingRdProfile what="alerts" />;

    return (
      <QueryErrorState
        error={read.error}
        notFoundText="Your alerts are not available"
        onRetry={() => void read.refetch()}
      />
    );
  }

  if (!opened || !draft) return null;

  const changes = myAlertsSave(opened, draft).changes;

  return (
    <MyAlertsEditor
      view={opened}
      draft={draft}
      zone={deviceTimezone()}
      status={status}
      problem={myAlertsProblem(draft)}
      dirty={Object.keys(changes).length > 0}
      onDraft={edit}
      onSave={() => save(opened, draft)}
    />
  );
}

function OwnDefaults(): ReactElement {
  const target: DefaultsTarget = { kind: "mine" };
  const read = useNotificationDefaults(target);

  if (read.isError && isMissingRdProfile(read.error)) {
    return <MissingRdProfile what="Client defaults" />;
  }

  return <DefaultsSection target={target} layer="rd" idPrefix="mine" read={read} />;
}

function PracticeDefaultsCard(): ReactElement {
  const practice = useNotificationDefaults({ kind: "practice" });

  return (
    <SettingsCard
      id="practice-defaults"
      title="Practice defaults"
      description="What every Client gets by email unless their RD or the Client chose otherwise."
    >
      <DefaultsSection
        target={{ kind: "practice" }}
        layer="practice"
        idPrefix="practice"
        read={practice}
      />
      <RdDefaults />
    </SettingsCard>
  );
}

function RdDefaults(): ReactElement {
  const rds = useNotificationDefaultsRds(true);
  const [rdId, setRdId] = useState("");
  const target: DefaultsTarget | null = rdId ? { kind: "rd", rdId } : null;
  const read = useNotificationDefaults(target);

  return (
    <div
      className="mt-8 border-t border-border pt-6"
      data-settings-section="rd-defaults"
    >
      <h3 className="font-sans text-body font-semibold text-foreground">
        An RD&apos;s defaults for their Clients
      </h3>
      <p className="mt-1 text-body-sm text-muted-foreground">
        Choose an RD to see and change what their Clients get.
      </p>
      <div className="mt-4">
        {rds.isError ? (
          <QueryErrorState
            error={rds.error}
            notFoundText="RDs are not available"
            onRetry={() => void rds.refetch()}
          />
        ) : (
          <RdPicker rds={rds.data ?? []} value={rdId} onChange={setRdId} />
        )}
      </div>
      {target ? (
        <div className="mt-6">
          <DefaultsSection
            key={rdId}
            target={target}
            layer="rd"
            idPrefix={`rd-${rdId}`}
            read={read}
          />
        </div>
      ) : null}
    </div>
  );
}

type DefaultsRead = ReturnType<typeof useNotificationDefaults>;

function saveFailure(error: unknown): string {
  const body = (error as { body?: { error?: unknown } } | null)?.body;

  return typeof body?.error === "string" ? body.error : SAVE_FAILED;
}

/** The editor's state over one layer: what it opened, its draft and its save. */
function useDefaultsEditor(
  target: DefaultsTarget,
  layer: EditorLayer,
  read: DefaultsRead
) {
  const { mutation, replace } = useSaveNotificationDefaults(target);
  const [opened, setOpened] = useState<NotificationDefaultsResponse | null>(null);
  const [draft, setDraft] = useState<DefaultsDraft>({});
  const [status, setStatus] = useState<SaveStatus>({ kind: "idle" });

  useEffect(() => {
    if (!read.data) return;

    setOpened(read.data);
    setDraft(startingDraft(read.data, layer));
  }, [read.data, layer]);

  const save = (
    from: NotificationDefaultsResponse,
    changes: DefaultsChange[]
  ): void => {
    setStatus({ kind: "saving" });
    mutation.mutate(changes, {
      onSuccess: (saved) => {
        setStatus({
          kind: "saved",
          changedByOthers: changedByOthers(from, saved, changes),
        });
        replace(saved);
      },
      onError: (error) => {
        const current = conflictCurrent(error);
        setStatus(
          current === null
            ? { kind: "failed", message: saveFailure(error) }
            : { kind: "conflict" }
        );
        if (current !== null) replace(current);
      },
    });
  };

  const edit = (key: string, next: DraftRow): void => {
    setDraft((current) => ({ ...current, [key]: next }));
    // A conflict or failure notice stays until the next save answers.
    if (status.kind === "saved") setStatus({ kind: "idle" });
  };

  return { opened, draft, status, save, edit };
}

/** The editor over one layer: its draft, its save, and what the save answered. */
function DefaultsSection({
  target,
  layer,
  idPrefix,
  read,
}: {
  target: DefaultsTarget;
  layer: EditorLayer;
  idPrefix: string;
  read: DefaultsRead;
}): ReactElement | null {
  const { opened, draft, status, save, edit } = useDefaultsEditor(target, layer, read);

  if (read.isError) {
    return (
      <QueryErrorState
        error={read.error}
        notFoundText="These settings are not available"
        onRetry={() => void read.refetch()}
      />
    );
  }

  if (!opened) return null;

  const changes = draftChanges(opened, layer, draft);

  return (
    <DefaultsEditor
      idPrefix={idPrefix}
      layer={layer}
      view={opened}
      draft={draft}
      status={status}
      problem={draftProblem(opened, layer, draft)}
      dirty={changes.length > 0}
      onDraft={edit}
      onSave={() => save(opened, changes)}
    />
  );
}
